import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthProvider'
import {
  getOnboardingApiClient,
  type OnboardingApiClient,
} from '../onboarding/onboarding-api'
import type { PmsCapabilityResponse } from '../onboarding/pms-capability-contracts'
import { WorkingHoursEditor, type PolicyBaseline } from './WorkingHoursEditor'
import type { SchedulingPolicyPayload } from './settings-contracts'
import { schedulingPolicyPayloadSchema } from './settings-contracts'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'

export function informationalHoursBaseline(
  data: PmsCapabilityResponse,
  tenant: string
): PolicyBaseline {
  if (
    data.tenantUid !== tenant ||
    !['PMS_INTENTIONALLY_DISABLED', 'PMS_CONNECTION_IN_PROGRESS'].includes(
      data.pmsStatus
    ) ||
    (data.clinicHours && data.clinicHours.tenantUid !== tenant)
  )
    throw new Error('Informational hours authority unavailable')
  const hours = data.clinicHours
  const workingHours = Object.fromEntries(
    ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day) => {
      const rule = hours?.workingHours[day as keyof typeof hours.workingHours]
      return [
        day,
        rule
          ? rule.status === 'CLOSED'
            ? '00:00-00:00'
            : `${rule.start}-${rule.end}`
          : (data.profile?.workingHours?.[
              day as keyof NonNullable<typeof data.profile>['workingHours']
            ] ?? '00:00-00:00'),
      ]
    })
  )
  const payload = schedulingPolicyPayloadSchema.parse({
    workingHours,
    breaks: (hours?.breaks ?? []).map((b, i) => ({
      ...b,
      name: b.name ?? `Break ${i + 1}`,
    })),
    specialDays: (hours?.specialDays ?? []).map((d, i) => ({
      ...d,
      name: d.name ?? `Special hours ${i + 1}`,
    })),
  })
  return {
    ...payload,
    workingHours: {
      mon: payload.workingHours.mon,
      tue: payload.workingHours.tue,
      wed: payload.workingHours.wed,
      thu: payload.workingHours.thu,
      fri: payload.workingHours.fri,
      sat: payload.workingHours.sat,
      sun: payload.workingHours.sun,
    },
    timeZone: hours?.timeZone ?? data.profile?.timeZone ?? '',
    policyRevision: hours?.revision ?? 0,
  }
}

export function informationalHoursCommand(
  data: PmsCapabilityResponse,
  tenant: string,
  payload: SchedulingPolicyPayload,
  timeZone: string
) {
  informationalHoursBaseline(data, tenant)
  const valid = schedulingPolicyPayloadSchema.parse(payload)
  new Intl.DateTimeFormat('en', { timeZone })
  if (!timeZone) throw new Error('Timezone required')
  return {
    action: 'SAVE_HOURS',
    expectedRevision: data.revision,
    expectedHoursRevision: data.clinicHours?.revision ?? 0,
    timeZone,
    workingHours: Object.fromEntries(
      Object.entries(valid.workingHours).map(([day, interval]) => [
        day,
        interval === '00:00-00:00'
          ? { status: 'CLOSED' }
          : {
              status: 'OPEN',
              start: interval.slice(0, 5),
              end: interval.slice(6),
            },
      ])
    ),
    breaks: valid.breaks,
    specialDays: valid.specialDays,
  }
}

export function InformationalWorkingHoursSettings({
  tenantUid,
  tenantLabel = 'this clinic',
  client = getOnboardingApiClient(),
}: {
  tenantUid: string
  tenantLabel?: string
  client?: Pick<OnboardingApiClient, 'pmsCapability'>
}) {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const queries = useQueryClient()
  const key = ['pms-capability', user?.uid, tenantUid]
  const query = useQuery({
    queryKey: key,
    enabled: !!user,
    queryFn: () => client.pmsCapability!(user!, tenantUid),
  })
  const baseline = useMemo(() => {
    try {
      return query.data
        ? informationalHoursBaseline(query.data, tenantUid)
        : null
    } catch {
      return null
    }
  }, [query.data, tenantUid])
  if (query.isPending) return <Spinner label="Loading clinic hours" />
  if (!user || !query.data || !baseline)
    return (
      <StatePanel
        kind="error"
        title="Clinic hours unavailable"
        description="Refresh and check your clinic access and scheduling authority."
      />
    )
  const data = query.data
  return (
    <WorkingHoursEditor
      key={`${user.uid}-${tenantUid}`}
      tenantId={tenantUid}
      tenantLabel={tenantLabel}
      informationalAdapter={{
        baseline,
        save: async (tenant, payload, timeZone) => {
          if (tenant !== tenantUid) throw new Error('Clinic changed')
          const saved = await client.pmsCapability!(
            user,
            tenant,
            informationalHoursCommand(data, tenant, payload, timeZone)
          )
          const result = informationalHoursBaseline(saved, tenant)
          queries.setQueryData(key, saved)
          void queries.invalidateQueries({ queryKey: ['onboarding'] })
          return result
        },
      }}
    />
  )
}
